import { cloneElement, isValidElement, useId } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactElement, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { ApiError } from "../api/client";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");
export { cx };

type Variant = "primary" | "secondary" | "ghost" | "danger";

export function Button({ variant = "primary", className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  const styles: Record<Variant, string> = {
    primary: "bg-accent text-accent-fg hover:opacity-90",
    secondary: "bg-surface-2 text-fg hover:bg-line",
    ghost: "text-muted hover:text-fg hover:bg-surface-2",
    danger: "bg-no text-white hover:opacity-90",
  };
  return (
    <button
      type="button"
      {...props}
      className={cx(
        "inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
        styles[variant],
        className,
      )}
    />
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cx("rounded-xl border border-line bg-surface p-4", className)}>{children}</section>;
}

/** A labelled form control. The hint is linked with aria-describedby, so the
 * control's accessible name is just the label. */
export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactElement<{ id?: string; "aria-describedby"?: string }> }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const control = isValidElement(children)
    ? cloneElement(children, { id: children.props.id ?? id, "aria-describedby": hint ? hintId : undefined })
    : children;
  return (
    <div className="space-y-1.5">
      <label htmlFor={children.props.id ?? id} className="block text-sm font-medium">{label}</label>
      {control}
      {hint && <p id={hintId} className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-line bg-surface px-3 py-2 text-base text-fg placeholder:text-muted";

export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={cx(inputCls, "min-h-10", p.className)} />;
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea rows={3} {...p} className={cx(inputCls, p.className)} />;
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => <select {...p} className={cx(inputCls, "min-h-10", p.className)} />;

export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "accent" | "yes" | "no" | "warn"; children: ReactNode }) {
  const tones = {
    neutral: "bg-surface-2 text-muted",
    accent: "bg-accent/15 text-accent",
    yes: "bg-yes/15 text-yes",
    no: "bg-no/15 text-no",
    warn: "bg-warn/15 text-warn",
  };
  return <span className={cx("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", tones[tone])}>{children}</span>;
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
  return (
    <p role="alert" className="rounded-lg border border-no/30 bg-no/10 px-3 py-2 text-sm text-no">
      {msg}
    </p>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-line border-t-accent" />
      {label}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-sm text-muted">{children}</p>;
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: "yes" | "no" }) {
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className={cx("text-lg font-semibold", tone === "yes" && "text-yes", tone === "no" && "text-no")}>{value}</div>
    </div>
  );
}

/** Loading / error / content switch for a query. */
export function QueryState({ q, children }: { q: { isPending: boolean; error: unknown }; children: () => ReactNode }) {
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  return <>{children()}</>;
}
